import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Plus, Edit, Trash2, X, Check, Eye, EyeOff, Shield,
  ShoppingCart, Wrench, BookOpen, Loader2, KeyRound, UserPlus, Search,
} from 'lucide-react';

const ROLES = [
  { id: 'super_admin', label: 'Admin', desc: 'Acces total + gestion utilisateurs', icon: Shield, color: '#00E01A' },
  { id: 'commercial', label: 'Commercial', desc: 'Prospection, clients, devis, configurateur', icon: ShoppingCart, color: '#3B82F6' },
  { id: 'technician', label: 'Technicien', desc: 'Exploitation, SAV, materiel, prets', icon: Wrench, color: '#F59E0B' },
  { id: 'read_only', label: 'Lecture seule', desc: 'Consultation uniquement', icon: BookOpen, color: '#8C8F94' },
];

const getRoleInfo = (roleId) => ROLES.find(r => r.id === roleId) || ROLES[3];

export default function UserManagement() {
  const { api, isSuperAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', password: '', role: 'commercial',
  });
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const resetForm = () => {
    setForm({ first_name: '', last_name: '', email: '', phone: '', password: '', role: 'commercial' });
    setEditingUser(null);
    setShowForm(false);
  };

  const openEdit = (u) => {
    setForm({ first_name: u.first_name, last_name: u.last_name, email: u.email, phone: u.phone || '', password: '', role: u.role });
    setEditingUser(u);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    if (!editingUser && form.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caracteres');
      return;
    }
    setSaving(true);
    try {
      if (editingUser) {
        const update = { first_name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone, role: form.role };
        await api.put(`/users/${editingUser.id}`, update);
        toast.success('Utilisateur mis a jour');
      } else {
        await api.post('/auth/register', form);
        toast.success('Utilisateur cree');
      }
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? 'Utilisateur desactive' : 'Utilisateur active');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Supprimer ${u.first_name} ${u.last_name} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Utilisateur supprime');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const changePassword = async () => {
    if (newPassword.length < 6) { toast.error('Minimum 6 caracteres'); return; }
    setSaving(true);
    try {
      await api.put(`/users/${showPasswordModal.id}/password`, { password: newPassword });
      toast.success('Mot de passe modifie');
      setShowPasswordModal(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const s = search.toLowerCase();
    return u.first_name?.toLowerCase().includes(s) || u.last_name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s);
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#00E01A]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#142915] flex items-center justify-center">
            <Users className="w-5 h-5 text-[#00E01A]" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Gestion des utilisateurs</h2>
            <p className="text-[#8C8F94] text-sm">{users.length} utilisateur{users.length > 1 ? 's' : ''}</p>
          </div>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 bg-[#00E01A] hover:bg-[#00B815] text-black font-semibold px-4 py-2.5 rounded-xl transition-all text-sm"
          data-testid="add-user-btn"
        >
          <UserPlus className="w-4 h-4" />
          Ajouter
        </button>
      </div>

      {/* Role Legend */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ROLES.map(r => {
          const count = users.filter(u => u.role === r.id).length;
          return (
            <div key={r.id} className="bg-[#1D2327] border border-[#2C3338] rounded-xl p-3" data-testid={`role-stat-${r.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <r.icon className="w-4 h-4" style={{ color: r.color }} />
                <span className="text-sm font-medium text-white">{r.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{count}</p>
              <p className="text-[10px] text-[#8C8F94]">{r.desc}</p>
            </div>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8F94]" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher un utilisateur..."
          className="w-full pl-10 pr-4 py-2.5 bg-[#1D2327] border border-[#2C3338] rounded-xl text-white text-sm focus:border-[#00E01A] focus:outline-none"
          data-testid="user-search"
        />
      </div>

      {/* Users List */}
      <div className="space-y-2">
        {filtered.map(u => {
          const role = getRoleInfo(u.role);
          const isCurrentUser = u.id === currentUser?.id;
          return (
            <div
              key={u.id}
              className={`bg-[#1D2327] border rounded-xl p-4 flex items-center gap-4 transition-all ${
                !u.is_active ? 'border-[#2C3338] opacity-50' : 'border-[#2C3338] hover:border-[#00E01A]/30'
              }`}
              data-testid={`user-row-${u.id}`}
            >
              {/* Avatar */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                style={{ backgroundColor: `${role.color}20`, color: role.color }}
              >
                {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-white font-medium text-sm truncate">{u.first_name} {u.last_name}</p>
                  {isCurrentUser && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#00E01A]/15 text-[#00E01A] font-medium">Vous</span>}
                  {!u.is_active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-medium">Inactif</span>}
                </div>
                <p className="text-[#8C8F94] text-xs truncate">{u.email}</p>
              </div>

              {/* Role Badge */}
              <div
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium flex-shrink-0"
                style={{ backgroundColor: `${role.color}15`, color: role.color }}
              >
                <role.icon className="w-3 h-3" />
                {role.label}
              </div>

              {/* Phone */}
              {u.phone && <p className="text-[#8C8F94] text-xs hidden md:block flex-shrink-0">{u.phone}</p>}

              {/* Franchise */}
              {u.franchise_name && (
                <p className="text-[#00E01A] text-xs hidden lg:block flex-shrink-0 max-w-[120px] truncate">{u.franchise_name}</p>
              )}

              {/* Actions */}
              {!isCurrentUser && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => openEdit(u)} className="p-2 text-[#8C8F94] hover:text-white hover:bg-white/5 rounded-lg transition-colors" data-testid={`edit-user-${u.id}`}>
                    <Edit className="w-4 h-4" />
                  </button>
                  {isSuperAdmin() && (
                    <button onClick={() => setShowPasswordModal(u)} className="p-2 text-[#8C8F94] hover:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors" data-testid={`password-user-${u.id}`}>
                      <KeyRound className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => toggleActive(u)} className={`p-2 rounded-lg transition-colors ${u.is_active ? 'text-[#8C8F94] hover:text-orange-400 hover:bg-orange-500/10' : 'text-[#8C8F94] hover:text-green-400 hover:bg-green-500/10'}`} data-testid={`toggle-user-${u.id}`}>
                    {u.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  {isSuperAdmin() && u.role !== 'super_admin' && (
                    <button onClick={() => deleteUser(u)} className="p-2 text-[#8C8F94] hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-8 text-[#8C8F94]">Aucun utilisateur trouve</div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => resetForm()}>
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-2xl w-full max-w-lg p-6 space-y-5" onClick={e => e.stopPropagation()} data-testid="user-form-modal">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{editingUser ? 'Modifier' : 'Nouvel utilisateur'}</h3>
              <button onClick={resetForm} className="p-1.5 text-[#8C8F94] hover:text-white rounded-lg hover:bg-white/5"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#8C8F94] mb-1">Prenom *</label>
                  <input value={form.first_name} onChange={e => setForm(f => ({...f, first_name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" required data-testid="user-first-name" />
                </div>
                <div>
                  <label className="block text-xs text-[#8C8F94] mb-1">Nom *</label>
                  <input value={form.last_name} onChange={e => setForm(f => ({...f, last_name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" required data-testid="user-last-name" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#8C8F94] mb-1">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" required data-testid="user-email" />
              </div>
              <div>
                <label className="block text-xs text-[#8C8F94] mb-1">Telephone</label>
                <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none" data-testid="user-phone" />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-xs text-[#8C8F94] mb-1">Mot de passe * (min 6 car.)</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none pr-10" required minLength={6} data-testid="user-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8F94] hover:text-white">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs text-[#8C8F94] mb-2">Role *</label>
                <div className="grid grid-cols-2 gap-2">
                  {ROLES.map(r => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setForm(f => ({...f, role: r.id}))}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        form.role === r.id
                          ? 'border-[#00E01A] bg-[#142915]'
                          : 'border-[#2C3338] bg-[#141617] hover:border-[#8C8F94]'
                      }`}
                      data-testid={`role-select-${r.id}`}
                    >
                      <r.icon className="w-4 h-4 flex-shrink-0" style={{ color: r.color }} />
                      <div>
                        <p className="text-sm font-medium text-white">{r.label}</p>
                        <p className="text-[10px] text-[#8C8F94]">{r.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={resetForm} className="flex-1 px-4 py-2.5 border border-[#2C3338] text-[#8C8F94] rounded-xl text-sm hover:text-white hover:border-[#8C8F94] transition-colors">
                  Annuler
                </button>
                <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00E01A] text-black font-semibold rounded-xl text-sm hover:bg-[#00B815] disabled:opacity-50 transition-all" data-testid="user-form-submit">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  {editingUser ? 'Enregistrer' : 'Creer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => { setShowPasswordModal(null); setNewPassword(''); }}>
          <div className="bg-[#1D2327] border border-[#2C3338] rounded-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="password-modal">
            <h3 className="text-lg font-bold text-white">Changer le mot de passe</h3>
            <p className="text-sm text-[#8C8F94]">{showPasswordModal.first_name} {showPasswordModal.last_name}</p>
            <div>
              <label className="block text-xs text-[#8C8F94] mb-1">Nouveau mot de passe (min 6 car.)</label>
              <input
                type="text"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                className="w-full px-3 py-2.5 bg-[#141617] border border-[#2C3338] rounded-lg text-white text-sm focus:border-[#00E01A] focus:outline-none"
                autoFocus
                data-testid="new-password-input"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => { setShowPasswordModal(null); setNewPassword(''); }} className="flex-1 px-4 py-2.5 border border-[#2C3338] text-[#8C8F94] rounded-xl text-sm hover:text-white">Annuler</button>
              <button onClick={changePassword} disabled={saving || newPassword.length < 6} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00E01A] text-black font-semibold rounded-xl text-sm hover:bg-[#00B815] disabled:opacity-50" data-testid="change-password-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Changer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
